import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  AlertCircle,
  RefreshCcw,
  Link2Off,
  Calendar,
  Mail,
  FileText,
} from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useConnectedAccounts,
  useConnectAccount,
  useDisconnectAccount,
} from "@/hooks/use-connected-accounts";

// ─── Provider logos ──────────────────────────────────────────────────────────

function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
    </svg>
  );
}

// ─── Capability definitions ─────────────────────────────────────────────────

interface CapabilityDef {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  scopes: string[];
}

const GOOGLE_CAPABILITIES: CapabilityDef[] = [
  {
    key: "calendar",
    icon: Calendar,
    label: "Calendar",
    description:
      "See where you need to be today, let Adva optimize your schedule, and auto-create or reschedule appointments as things change.",
    scopes: [
      "https://www.googleapis.com/auth/calendar.events",
      "https://www.googleapis.com/auth/calendar.freebusy",
    ],
  },
  {
    key: "email",
    icon: Mail,
    label: "Email",
    description:
      "Keep opportunities updated with the latest customer emails, send pre-appointment reminders, and follow up directly from Adva.",
    scopes: [
      "https://www.googleapis.com/auth/gmail.send",
      "https://www.googleapis.com/auth/gmail.readonly",
    ],
  },
  {
    key: "drive",
    icon: FileText,
    label: "Drive",
    description:
      "Generate proposals and documents as Google Docs or Slides, stored in your Drive.",
    scopes: ["https://www.googleapis.com/auth/drive.file"],
  },
];

function isCapabilityConnected(
  grantedScopes: string[],
  requiredScopes: string[],
): boolean {
  return requiredScopes.every((s) => grantedScopes.includes(s));
}

interface FeatureItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
}

const MICROSOFT_FEATURES: FeatureItem[] = [
  {
    icon: Calendar,
    label: "Calendar",
    description: "Sync your Outlook calendar with Adva scheduling.",
  },
  {
    icon: Mail,
    label: "Email",
    description:
      "Surface customer emails in opportunity context and send follow-ups from Outlook.",
  },
];

const APPLE_FEATURES: FeatureItem[] = [
  {
    icon: Calendar,
    label: "Calendar",
    description: "Sync your iCloud calendar with Adva scheduling.",
  },
  {
    icon: Mail,
    label: "Email",
    description:
      "Surface customer emails from Apple Mail in opportunity context.",
  },
];

// ─── Google card with capability checkboxes ─────────────────────────────────

interface GoogleCardProps {
  grantedScopes: string[];
  isConnected: boolean;
  needsReauth: boolean;
  externalAccountId: string | null;
  onConnect: (capabilities: string[]) => void;
  onDisconnect: () => void;
  isConnecting: boolean;
}

function GoogleCard({
  grantedScopes,
  isConnected,
  needsReauth,
  externalAccountId,
  onConnect,
  onDisconnect,
  isConnecting,
}: GoogleCardProps) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(GOOGLE_CAPABILITIES.map((c) => c.key)),
  );

  const capabilityStatus = GOOGLE_CAPABILITIES.map((cap) => ({
    ...cap,
    connected: isCapabilityConnected(grantedScopes, cap.scopes),
  }));

  const anyConnected = capabilityStatus.some((c) => c.connected);
  const hasNewSelection = capabilityStatus.some(
    (c) => !c.connected && selected.has(c.key),
  );

  function toggleCapability(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleConnect() {
    const caps = capabilityStatus
      .filter((c) => !c.connected && selected.has(c.key))
      .map((c) => c.key);
    if (caps.length > 0) onConnect(caps);
  }

  return (
    <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[color:var(--color-border)] bg-white">
          <GoogleLogo className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="typo-body text-sm font-medium text-[color:var(--color-text-hi)]">
              Google
            </p>
            {isConnected && !needsReauth && (
              <Badge
                variant="outline"
                className="gap-1 border-[color:var(--color-success)]/30 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]"
              >
                <CheckCircle2 className="h-3 w-3" />
                Connected
              </Badge>
            )}
            {needsReauth && (
              <Badge
                variant="outline"
                className="gap-1 border-destructive/30 bg-destructive/10 text-destructive"
              >
                <AlertCircle className="h-3 w-3" />
                Reconnect required
              </Badge>
            )}
          </div>
          {isConnected && externalAccountId && (
            <p className="typo-body text-xs text-[color:var(--color-text-lo)]">
              {externalAccountId}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isConnected && needsReauth && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onConnect(GOOGLE_CAPABILITIES.map((c) => c.key))}
              disabled={isConnecting}
            >
              <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
              Reconnect
            </Button>
          )}
          {isConnected && !needsReauth && (
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0 text-[color:var(--color-text-lo)] hover:text-destructive"
              onClick={onDisconnect}
            >
              <Link2Off className="mr-1.5 h-3.5 w-3.5" />
              Disconnect
            </Button>
          )}
        </div>
      </div>
      <div className="border-t border-[color:var(--color-border)] px-4 py-3">
        <ul className="space-y-3">
          {capabilityStatus.map((cap) => (
            <li key={cap.key} className="flex items-start gap-3">
              {cap.connected ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--color-success)]" />
              ) : (
                <Checkbox
                  checked={selected.has(cap.key)}
                  onCheckedChange={() => toggleCapability(cap.key)}
                  className="mt-0.5"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <cap.icon className="h-3.5 w-3.5 shrink-0 text-[color:var(--color-text-lo)]" />
                  <span className="typo-body text-sm font-medium text-[color:var(--color-text-hi)]">
                    {cap.label}
                  </span>
                  {cap.connected && (
                    <span className="typo-body text-xs text-[color:var(--color-success)]">
                      Active
                    </span>
                  )}
                </div>
                <p className="mt-0.5 typo-body text-xs text-[color:var(--color-text-md)]">
                  {cap.description}
                </p>
              </div>
            </li>
          ))}
        </ul>
        {(!anyConnected || hasNewSelection) && !needsReauth && (
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={isConnecting || !hasNewSelection}
            >
              {isConnecting
                ? "Connecting…"
                : anyConnected
                  ? "Add Permissions"
                  : "Connect"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Coming-soon provider card ──────────────────────────────────────────────

interface ComingSoonCardProps {
  name: string;
  logo: React.ReactNode;
  features: FeatureItem[];
}

function ComingSoonCard({ name, logo, features }: ComingSoonCardProps) {
  return (
    <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[color:var(--color-border)] bg-white">
          {logo}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="typo-body text-sm font-medium text-[color:var(--color-text-hi)]">
              {name}
            </p>
            <Badge
              variant="outline"
              className="text-[color:var(--color-text-lo)]"
            >
              Coming soon
            </Badge>
          </div>
        </div>
      </div>
      <div className="border-t border-[color:var(--color-border)] px-4 py-3">
        <ul className="space-y-2">
          {features.map((f) => (
            <li key={f.label} className="flex items-start gap-2.5">
              <f.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[color:var(--color-text-lo)]" />
              <p className="typo-body text-xs text-[color:var(--color-text-md)]">
                <span className="font-medium text-[color:var(--color-text-hi)]">
                  {f.label}
                </span>{" "}
                — {f.description}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ConnectedAccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnectingProvider, setDisconnectingProvider] = useState<
    string | null
  >(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [flashMessage, setFlashMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const { data, isLoading, error } = useConnectedAccounts();
  const connectMutation = useConnectAccount();
  const disconnectMutation = useDisconnectAccount();

  useEffect(() => {
    const connected = searchParams.get("connected");
    const errorParam = searchParams.get("error");

    if (connected) {
      setFlashMessage({
        type: "success",
        text: `${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully.`,
      });
      setSearchParams({}, { replace: true });
    } else if (errorParam) {
      setFlashMessage({
        type: "error",
        text:
          errorParam === "auth_failed"
            ? "Connection failed. Please try again."
            : `Connection error: ${errorParam}`,
      });
      setSearchParams({}, { replace: true });
    }
  }, []);

  async function handleConnectGoogle(capabilities: string[]) {
    setIsConnecting(true);
    try {
      const result = await connectMutation.mutateAsync({
        provider: "google",
        capabilities,
      });
      window.location.href = result.auth_url;
    } catch {
      setFlashMessage({ type: "error", text: "Failed to start connection." });
      setIsConnecting(false);
    }
  }

  async function handleDisconnectConfirm() {
    if (!disconnectingProvider) return;
    try {
      await disconnectMutation.mutateAsync(disconnectingProvider);
      setFlashMessage({ type: "success", text: "Account disconnected." });
    } catch {
      setFlashMessage({ type: "error", text: "Failed to disconnect." });
    } finally {
      setDisconnectingProvider(null);
    }
  }

  const googleConnection =
    (data?.accounts ?? []).find((a) => a.provider === "google") ?? null;
  const googleConnected =
    !!googleConnection && googleConnection.status !== "disconnected";
  const googleNeedsReauth =
    googleConnected &&
    (googleConnection.status === "reauth_required" ||
      googleConnection.status === "error");

  return (
    <SettingsPage
      title="Connected Accounts"
      subtitle="Link your personal accounts so Adva can work with your calendar, email, and files."
    >
      {flashMessage && (
        <div
          className={`mb-4 rounded-md px-4 py-3 typo-body text-sm ${
            flashMessage.type === "success"
              ? "bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]"
              : "bg-destructive/10 text-destructive"
          }`}
        >
          {flashMessage.text}
        </div>
      )}

      {isLoading && (
        <p className="typo-body text-sm text-[color:var(--color-text-lo)]">
          Loading…
        </p>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 typo-body text-sm text-destructive">
          Failed to load connected accounts.
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-3">
          <GoogleCard
            grantedScopes={googleConnection?.scopes ?? []}
            isConnected={googleConnected}
            needsReauth={googleNeedsReauth}
            externalAccountId={googleConnection?.external_account_id ?? null}
            onConnect={handleConnectGoogle}
            onDisconnect={() => setDisconnectingProvider("google")}
            isConnecting={isConnecting}
          />

          <ComingSoonCard
            name="Microsoft"
            logo={<MicrosoftLogo className="h-4 w-4" />}
            features={MICROSOFT_FEATURES}
          />

          <ComingSoonCard
            name="Apple"
            logo={<AppleLogo className="h-4 w-4" />}
            features={APPLE_FEATURES}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!disconnectingProvider}
        onOpenChange={(open) => !open && setDisconnectingProvider(null)}
        title="Disconnect Google?"
        description="All Google permissions (calendar, email, files) will be removed. You can reconnect at any time."
        confirmLabel="Disconnect"
        onConfirm={handleDisconnectConfirm}
      />
    </SettingsPage>
  );
}
